import { useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Button } from '$components'
import { Card } from '$components'
import { appointmentPublicCancelMutation } from '$api-client/@tanstack/react-query.gen'
import { Text } from '$components'
import { CheckIcon, WarningIcon, XIcon, LogoIcon } from '$components'
import { ToastHelper } from '$components'

export function CancelAppointmentPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()

  const signature = searchParams.get('signature')
  const expires = searchParams.get('expires')

  const { mutate: cancelAppointment, isPending, isSuccess, isError } = useMutation({
    ...appointmentPublicCancelMutation(),
    onError: (error) => ToastHelper.error(error?.message || 'Failed to cancel appointment. Please contact the business.'),
  })

  const handleCancel = () => {
    if (!id || !signature || !expires) {
      ToastHelper.error('Invalid or expired link')
      return
    }

    cancelAppointment({
      path: { id: Number(id) },
      body: {
        signature: signature,
        expires: Number(expires),
      },
    })
  }

  useEffect(() => {
    if (!signature || !expires) {
      ToastHelper.error('Invalid or expired link')
    }
  }, [signature, expires])

  const isInvalidLink = !signature || !expires

  return (
    <div className="min-h-screen bg-surface-muted flex items-center justify-center p-stack-roomy">
      <div className="w-full max-w-prose-md">
        {/* Logo/Branding */}
        <div className="flex justify-center mb-stack-section">
          <LogoIcon className="h-6 w-auto text-fg-link" />
        </div>

        {/* Main Card */}
        <Card rounding="xl">
          {!isSuccess && !isError && (
            <Card.Content className="p-stack-grid sm:p-stack-hero border border-border rounded-card">
              <div className="flex flex-col items-center text-center mb-stack-grid">
                <XIcon className="size-10 text-danger-fg mb-stack-section" />

                <Text
                  type="h2"
                  className="text-fg mb-stack"
                >
                  Cancel Your Appointment
                </Text>

                <Text
                  type="s"
                  className="text-fg-subtle max-w-prose-sm"
                >
                  We&apos;re sorry to hear you need to cancel. The business will be notified immediately.
                </Text>
              </div>

              <Button
                onClick={handleCancel}
                isLoading={isPending}
                disabled={isPending || isInvalidLink}
                variant="primary"
                className="w-full h-12 text-body"
              >
                {isPending ? 'Cancelling...' : 'Cancel Appointment'}
              </Button>
            </Card.Content>
          )}

          {isSuccess && (
            <Card.Content className="p-stack-grid sm:p-stack-hero flex flex-col border border-border items-center text-center rounded-card">
              <CheckIcon className="size-10 text-success mb-stack-section" />

              <Text
                type="h2"
                className="text-fg mb-stack"
              >
                Appointment Cancelled
              </Text>

              <Text
                type="s"
                className="text-fg-subtle max-w-prose-sm mb-stack-grid"
              >
                Your appointment has been successfully cancelled. The business has been notified and we hope to see you again soon.
              </Text>
            </Card.Content>
          )}

          {isError && (
            <Card.Content className="p-stack-grid sm:p-stack-hero flex flex-col border border-border items-center text-center rounded-card">
              <WarningIcon className="size-10 text-danger mb-stack-section" />

              <Text
                type="h2"
                className="text-fg mb-stack"
              >
                Unable to Cancel
              </Text>

              <Text
                type="s"
                className="text-fg-subtle max-w-prose-sm mb-stack-grid"
              >
                We couldn&apos;t cancel your appointment. Please contact the business directly for assistance.
              </Text>
            </Card.Content>
          )}
        </Card>

        {/* Footer Text */}
        <div className="text-center">
          <Text
            type="s"
            className="text-fg-subtle"
          >
            This is a secure cancellation link. Do not share it with others.
          </Text>
        </div>
      </div>
    </div>
  )
}
